import { useState, type Dispatch, type SetStateAction } from 'react'
import { Button, Modal, Space, Typography, message } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import {
  deleteChain,
  deleteChainBackground,
  deleteChainOriginal,
  deleteChainShadow,
  deleteChainTransparent,
  type AssetChainOut,
} from '../api/client'

/**
 * Phase 2B-2 §7.3 — the graduated "删除原图 / 删除透明底图 / 删除色底图 /
 * 删除阴影图 / 删除整条资产链" delete flow, extracted out of `Home.tsx` into
 * one hook so the standalone asset list and the SaaS Workspace Images page
 * share the *exact* same confirmation copy and cascade choices (spec:
 * "具体交互、确认信息和依赖关系：直接以标准版当前实现为准。不要凭印象重写")
 * instead of each page reinventing its own (riskier) single "删除全部"
 * button.
 */
export function useChainDeleteActions(
  loadChains: () => Promise<void>,
  setChains: Dispatch<SetStateAction<AssetChainOut[]>>,
  onDeleted?: (chainId: string) => void,
) {
  const { t } = useTranslation(['autoProcess', 'actions'])
  const [deleteChoice, setDeleteChoice] = useState<
    { kind: 'original' | 'transparent' | 'background'; chain: AssetChainOut } | null
  >(null)

  const onDeleteOriginal = (chain: AssetChainOut) => setDeleteChoice({ kind: 'original', chain })
  const onDeleteTransparent = (chain: AssetChainOut) => setDeleteChoice({ kind: 'transparent', chain })
  const onDeleteBackground = (chain: AssetChainOut) => setDeleteChoice({ kind: 'background', chain })

  const runDeleteChoice = async (cascade: boolean) => {
    if (!deleteChoice) return
    const { kind, chain } = deleteChoice
    setDeleteChoice(null)
    if (kind === 'original') {
      await deleteChainOriginal(chain.id, cascade)
      message.success(cascade ? t('deleteActions.deletedWholeChain') : t('deleteActions.deletedOriginalKeptDerivatives'))
      if (cascade) onDeleted?.(chain.id)
    } else if (kind === 'transparent') {
      await deleteChainTransparent(chain.id, cascade)
      message.success(
        cascade ? t('deleteActions.deletedTransparentAndDownstream') : t('deleteActions.deletedTransparentUpstreamMissing'),
      )
    } else {
      await deleteChainBackground(chain.id, cascade)
      message.success(cascade ? t('deleteActions.deletedBackgroundAndShadow') : t('deleteActions.deletedBackground'))
    }
    await loadChains()
  }

  const onDeleteShadow = async (chain: AssetChainOut) => {
    await deleteChainShadow(chain.id)
    message.success(t('deleteActions.deletedShadow'))
    await loadChains()
  }

  const onDeleteChain = (chain: AssetChainOut) => {
    Modal.confirm({
      centered: true,
      title: t('deleteActions.permanentDeleteTitle'),
      icon: <ExclamationCircleOutlined />,
      content: t('deleteActions.permanentDeleteContent', { filename: chain.original_filename }),
      okText: t('deleteActions.permanentDeleteOk'),
      okButtonProps: { danger: true },
      cancelText: t('actions:cancel'),
      onOk: () => {
        setChains((prev) => prev.filter((c) => c.id !== chain.id))
        onDeleted?.(chain.id)
        deleteChain(chain.id)
          .then(() => {
            message.success(t('deleteActions.permanentDeleteSuccess'))
          })
          .catch((e) => {
            message.error(
              t('deleteActions.permanentDeleteFailed', { message: (e as Error).message || t('actions:retry') }),
            )
            loadChains().catch(() => undefined)
          })
      },
    })
  }

  const deleteDialog = (
    <Modal
      title={
        deleteChoice?.kind === 'original'
          ? t('deleteActions.deleteOriginalTitle')
          : deleteChoice?.kind === 'transparent'
            ? t('deleteActions.deleteTransparentTitle')
            : t('deleteActions.deleteBackgroundTitle')
      }
      open={!!deleteChoice}
      onCancel={() => setDeleteChoice(null)}
      footer={null}
    >
      <Typography.Paragraph>
        {deleteChoice?.kind === 'original' && t('deleteActions.deleteOriginalHint')}
        {deleteChoice?.kind === 'transparent' && t('deleteActions.deleteTransparentHint')}
        {deleteChoice?.kind === 'background' && t('deleteActions.deleteBackgroundHint')}
      </Typography.Paragraph>
      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button onClick={() => setDeleteChoice(null)}>{t('actions:cancel')}</Button>
        <Button onClick={() => runDeleteChoice(false)}>
          {deleteChoice?.kind === 'original'
            ? t('deleteActions.onlyOriginal')
            : deleteChoice?.kind === 'transparent'
              ? t('deleteActions.onlyTransparent')
              : t('deleteActions.onlyBackground')}
        </Button>
        <Button danger type="primary" onClick={() => runDeleteChoice(true)}>
          {deleteChoice?.kind === 'original' ? t('deleteActions.deleteWholeChain') : t('deleteActions.deleteDownstream')}
        </Button>
      </Space>
    </Modal>
  )

  return { onDeleteOriginal, onDeleteTransparent, onDeleteBackground, onDeleteShadow, onDeleteChain, deleteDialog }
}
